/**
 * One-time forge_* → enferno_* localStorage / Preferences migration.
 *
 * Background: localStorage keys were originally prefixed `forge_*` (when the app
 * was named Forge). Forge is now a competing fitness app on Play Store, and the
 * brand has fully moved to Enferno. This migration renames all persisted keys
 * to `enferno_*` exactly once per device, preserving user data through the
 * transition.
 *
 * Failure mode: if migration is somehow skipped or fails on a given device,
 * the user sees a "fresh app" (no history, fresh profile) — their data is
 * still physically in storage under the old prefix. We never delete data
 * without copying it first, so re-running the migration would still recover it.
 *
 * Web path: runs synchronously at module import (before any other code reads
 * localStorage). The bootstrap path in AuthContext relies on `storageGetSync`
 * fetching the access token at module-load time, so this migration MUST be
 * imported before AuthContext.
 *
 * Native path: Capacitor `Preferences` is async. `runNativeMigration()` is
 * called from AuthProvider's first effect, before any read of the new keys.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const MIGRATION_FLAG = 'enferno_migration_v1_done';
const FORGE_PREFIX = 'forge_';
const ENFERNO_PREFIX = 'enferno_';

const renameKey = (k) => ENFERNO_PREFIX + k.slice(FORGE_PREFIX.length);

const runWebMigrationSync = () => {
  try {
    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem(MIGRATION_FLAG) === 'true') return;

    const forgeKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(FORGE_PREFIX)) forgeKeys.push(k);
    }
    for (const k of forgeKeys) {
      const v = localStorage.getItem(k);
      if (v != null) {
        const newKey = renameKey(k);
        // Don't overwrite an enferno_* key that already has content
        if (localStorage.getItem(newKey) == null) {
          localStorage.setItem(newKey, v);
        }
      }
      localStorage.removeItem(k);
    }
    localStorage.setItem(MIGRATION_FLAG, 'true');
    if (forgeKeys.length) {
      // eslint-disable-next-line no-console
      console.info(`[storage] Migrated ${forgeKeys.length} forge_* keys → enferno_*`);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[storage] Web migration failed:', err);
  }
};

export const runNativeMigration = async () => {
  try {
    if (!Capacitor?.isNativePlatform?.()) return;
    const { value: done } = await Preferences.get({ key: MIGRATION_FLAG });
    if (done === 'true') return;

    const { keys } = await Preferences.keys();
    const forgeKeys = (keys || []).filter((k) => k.startsWith(FORGE_PREFIX));
    for (const k of forgeKeys) {
      const { value } = await Preferences.get({ key: k });
      if (value != null) {
        const newKey = renameKey(k);
        const existing = await Preferences.get({ key: newKey });
        if (existing.value == null) {
          await Preferences.set({ key: newKey, value });
        }
      }
      await Preferences.remove({ key: k });
    }
    await Preferences.set({ key: MIGRATION_FLAG, value: 'true' });
    if (forgeKeys.length) {
      // eslint-disable-next-line no-console
      console.info(`[storage] Migrated ${forgeKeys.length} native forge_* keys → enferno_*`);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[storage] Native migration failed:', err);
  }
};

// Side effect: run the web migration immediately on first import.
runWebMigrationSync();
